import { PageLoadIndicator } from '@dreamstack/simple-components/src/lib/PageLoadIndicator'
import { TrackingSideEffects } from '@dreamstack/tracking'
import { cn } from '@dreamstack/util/src/cn'
import React from 'react'
import { ErrorBoundary } from '../error/ErrorBoundary'
import { IS_ACCENTRO_INVESTORS, IS_ACCENTRO_WEB } from '../config/config'
import { MainContainer } from './MainGrid'
import { Footer } from './Footer'
import type { HeroProps } from './Hero'
import { Hero } from './Hero'
import { useMainLayoutContext } from './MainLayoutContext'
import type { NavbarProps } from './NavBar'
import { NavBar } from './NavBar'
import { PageTag } from './PageTag'

const IS_INTERNATIONAL = !IS_ACCENTRO_WEB && !IS_ACCENTRO_INVESTORS

const Layout = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn('flex flex-col min-h-screen', className)}>{children}</div>
)

const MAIN_BACKGROUND_CLASS = 'bg-accentroWhite-full z-10 flex-1'

type MainLayoutProps = {
  navbarProps?: NavbarProps | null
  hero?: HeroProps | null
  subHero?: React.ReactNode | null
  standalone?: boolean | null
  showContactFooter?: boolean | null
  showNewsletterFooter?: boolean | null
  pageTag?: string | null
  className?: string
  removePaddingBottom?: boolean | null
}
// MainLayout component switched to plain const component and tailwind classes
export const MainLayout = ({
  children,
  navbarProps,
  hero,
  subHero,
  standalone,
  showContactFooter,
  showNewsletterFooter,
  pageTag,
  className,
  removePaddingBottom,
}: React.PropsWithChildren<MainLayoutProps>) => {
  const { contactFooter, newsLetterFooter } = useMainLayoutContext()
  const showNewsletter = showNewsletterFooter === null || showNewsletterFooter
  const showContact = showContactFooter === null || showContactFooter

  return (
    <Layout className={className}>
      <PageLoadIndicator />
      <TrackingSideEffects />
      <NavBar
        standalone={standalone ?? undefined}
        {...navbarProps}
        hasHero={!!hero}
        heroSize={!!hero ? hero.size : undefined}
      />
      {hero && <Hero {...hero} />}
      {subHero}
      <div className={MAIN_BACKGROUND_CLASS}>
        <MainContainer
          className={cn(
            'flex-1 mb-px-64 md:mb-px-80 lg:mb-px-96',
            removePaddingBottom && 'md:mb-0 lg:mb-0 mb-0'
          )}
        >
          {pageTag && (
            <div
              className={cn(
                (IS_ACCENTRO_WEB || IS_ACCENTRO_INVESTORS) &&
                  'mt-px-128 mb-px-24',
                IS_INTERNATIONAL && 'mt-px-80 -mb-px-24'
              )}
            >
              <PageTag text={pageTag} />
            </div>
          )}
          <ErrorBoundary>{children}</ErrorBoundary>
        </MainContainer>
        {!standalone && (
          <>
            {showNewsletter && newsLetterFooter}
            {showContact && contactFooter}
          </>
        )}
      </div>
      <Footer />
    </Layout>
  )
}
